/**
 * 移动端壳层：顶部 Header + 中间内容区 + 底部 TabBar。
 *
 * 路由 → 顶栏标题 / 返回箭头 / 搜索按钮样式：
 *   /home             →  标题「序章」；无返回；搜索 = icon-text（PRD §4.4）
 *   /album, /album/*  →  标题「时间相册」；显示返回；搜索 = icon
 *   /letters, /meet   →  标题对应中文；显示返回；搜索 = icon
 *   /search, /recycle → 标题对应中文；显示返回；搜索 = icon
 *   /manage, /manage/*→  标题「管理设置 / 子页」；显示返回；搜索 = icon
 *   /manage/approvals →  标题「审批中心」
 *   /account          →  标题「我」；显示返回；搜索 = icon
 *   /account/profile   →  标题「个人资料」；显示返回；搜索 = icon
 *   /account/status    →  标题「成长状态」；显示返回；搜索 = icon
 *   /account/name     →  标题「修改昵称」；显示返回；搜索 = icon
 *   /account/password →  标题「修改密码」；显示返回；搜索 = icon
 *   /account/details  →  标题「空间成员」；显示返回；搜索 = icon
 *   /account/animations → 标题「动效管理」；显示返回；搜索 = icon
 */

import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { BellIcon, HelpIcon } from './icons';
import { MobileHeader } from './MobileHeader';
import { MobileTabBar, type TabKey } from './MobileTabBar';
import { useUnreadBadge } from './unreadBadge';

function deriveTab(pathname: string): TabKey | null {
  if (pathname.startsWith('/album')) return 'album';
  if (pathname.startsWith('/letters')) return 'letters';
  if (pathname.startsWith('/account')) return 'account';
  return null;
}

function deriveHeaderTitle(pathname: string): string {
  if (pathname.startsWith('/album')) return '时间相册';
  if (pathname.startsWith('/letters')) return '那些话';
  if (pathname.startsWith('/meet')) return '相识相遇';
  if (pathname.startsWith('/help')) return '帮助';
  if (pathname.startsWith('/notifications')) return '通知';
  if (pathname.startsWith('/search')) return '搜索';
  if (pathname.startsWith('/recycle')) return '回收站';
  if (pathname.startsWith('/manage/inbox')) return '收到的请求';
  if (pathname.startsWith('/manage/sent')) return '我发起的';
  if (pathname.startsWith('/manage/settings')) return '空间设置';
  if (pathname.startsWith('/manage/approvals')) return '审批中心';
  if (pathname.startsWith('/manage')) return '管理设置';
  if (pathname.startsWith('/account/services')) return '创意天地';
  if (pathname.startsWith('/account/content')) return '我的内容';
  if (pathname.startsWith('/account/profile')) return '个人资料';
  if (pathname.startsWith('/account/status')) return '铭牌';
  if (pathname.startsWith('/account/name')) return '修改昵称';
  if (pathname.startsWith('/account/password')) return '修改密码';
  if (pathname.startsWith('/account/details')) return '空间成员';
  if (pathname.startsWith('/account/animations')) return '动效管理';
  if (pathname.startsWith('/account')) return '我';
  return '序章';
}

function deriveSearchMode(pathname: string): 'icon' | 'icon-text' {
  // PRD §4.4：仅首页显示「搜索」文字
  return pathname === '/home' || pathname === '/' ? 'icon-text' : 'icon';
}

function shouldShowBack(pathname: string): boolean {
  return pathname !== '/home' && pathname !== '/';
}

export const MobileShell: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const tab = deriveTab(location.pathname);
  const title = deriveHeaderTitle(location.pathname);
  const contentTab = new URLSearchParams(location.search).get('tab');
  const resolvedTitle = location.pathname.startsWith('/account/content')
    ? (contentTab === 'drafts' ? '我的草稿' : '我发布的内容')
    : title;
  const searchMode = deriveSearchMode(location.pathname);
  const showBack = shouldShowBack(location.pathname);
  const isHome = location.pathname === '/home' || location.pathname === '/';
  const isSearchPage = location.pathname.startsWith('/search');
  const isNotificationsPage = location.pathname.startsWith('/notifications');
  const { count: unreadCount } = useUnreadBadge();
  const showNotifications = !isHome && !isNotificationsPage;
  return (
    <div className="fp-mobile-app">
      <MobileHeader
        title={resolvedTitle}
        showBack={showBack}
        onBack={() => navigate(-1)}
        onHome={showBack ? () => navigate('/home') : undefined}
        searchMode={searchMode}
        onSearch={isSearchPage ? undefined : () => navigate('/search')}
        beforeSearch={showNotifications ? (
          <button
            type="button"
            className={`fp-mobile-icon-btn fp-mobile-header__notification${unreadCount > 0 ? ' fp-mobile-header__notification--unread' : ''}`}
            onClick={() => navigate('/notifications')}
            aria-label={unreadCount > 0 ? `通知，有 ${unreadCount} 条未读` : '通知'}
            title="通知"
            data-testid="header-notifications"
          >
            <BellIcon size={20} />
            {unreadCount > 0 ? (
              <span className="fp-mobile-icon-btn__dot" data-testid="header-notifications-dot" aria-hidden="true" />
            ) : null}
          </button>
        ) : null}
        trailing={isHome ? (
            <button
              type="button"
              className="fp-mobile-icon-btn"
              onClick={() => navigate('/help')}
              aria-label="帮助文档"
              title="帮助文档"
              data-testid="header-help"
            >
              <HelpIcon size={20} />
            </button>
          ) : null}
      />
      <main className="fp-mobile-content">
        <Outlet />
      </main>
      <MobileTabBar
        active={tab}
        onChange={(next) => {
          if (next === 'album') navigate('/album');
          else if (next === 'letters') navigate('/letters');
          else if (next === 'account') navigate('/account');
        }}
      />
    </div>
  );
};

export default MobileShell;
